import type { ButtonHTMLAttributes, MouseEvent, ReactNode, Ref } from "react";
import { classNames } from "./internal/class-names";

export type ButtonVariant = "danger" | "ghost" | "primary" | "secondary";

type NativeButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-busy">;

export interface ButtonProps extends NativeButtonProps {
  readonly variant?: ButtonVariant | undefined;
  /**
   * Marks an in-flight action. A busy button stays focusable but refuses to
   * fire, so keyboard users never lose their place mid-submit.
   */
  readonly busy?: boolean | undefined;
  /** Text announced and shown while busy. Falls back to the normal children. */
  readonly busyLabel?: ReactNode | undefined;
  readonly ref?: Ref<HTMLButtonElement> | undefined;
}

/**
 * The kit's action primitive. `type` defaults to `button`: an action must opt
 * in to submitting a form rather than doing it by accident.
 */
export function Button({
  "aria-disabled": ariaDisabled,
  busy = false,
  busyLabel,
  children,
  className,
  onClick,
  type = "button",
  variant = "secondary",
  ...rest
}: ButtonProps): ReactNode {
  function handleClick(event: MouseEvent<HTMLButtonElement>): void {
    if (busy) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  }

  return (
    <button
      {...rest}
      aria-busy={busy ? "true" : undefined}
      aria-disabled={busy ? "true" : ariaDisabled}
      className={classNames("thw-button", `thw-button--${variant}`, className)}
      onClick={handleClick}
      type={type}
    >
      {busy ? <span aria-hidden="true" className="thw-button__busy" /> : null}
      <span>{busy && busyLabel !== undefined ? busyLabel : children}</span>
    </button>
  );
}
